import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { sendPasswordReset as apiSendPasswordReset } from "./api";

export type AllowlistRole = "admin" | "user" | "executive";

export type IdentityProvider = "password" | "saml" | "oidc";

export interface AllowlistEntry {
  role: AllowlistRole;
  /** Email of the admin who first added this user. Stamped once at create time. */
  addedBy?: string;
  /** ISO timestamp set when the user was first added. */
  addedAt?: string;
  /** Email of the admin who last changed the role. Stamped on every edit. */
  lastModifiedBy?: string;
  /** ISO timestamp set on every role edit. */
  lastModifiedAt?: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /** True only after user is signed in AND present in `userAllowlist`. */
  allowlisted: boolean;
  /** The allowlist doc, including role. Null until verified. */
  allowlistEntry: AllowlistEntry | null;
  /** The user's role string, or null if not allowlisted. */
  role: AllowlistRole | null;
  /** True if the user's role is `admin`. */
  isAdmin: boolean;
  /** True if the user can write (admin or user). */
  canWrite: boolean;
  /** True if the user is an executive (read-only). */
  isExec: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  /**
   * SSO sign-in abstraction — placeholder until Entra ID is wired.
   * Throws "SSO sign-in not yet wired" for any provider.
   */
  signInWithSso: (provider: "saml" | "oidc") => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchAllowlistEntry(email: string): Promise<AllowlistEntry | null> {
  try {
    const snap = await getDoc(doc(db, "userAllowlist", email.toLowerCase()));
    if (!snap.exists()) return null;
    return snap.data() as AllowlistEntry;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [allowlistEntry, setAllowlistEntry] = useState<AllowlistEntry | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      setUser(currentUser);
      if (currentUser?.email) {
        const entry = await fetchAllowlistEntry(currentUser.email);
        setAllowlistEntry(entry);
      } else {
        setAllowlistEntry(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => {
      const role = allowlistEntry?.role ?? null;
      return {
        user,
        loading,
        allowlisted: !!user && !!allowlistEntry,
        allowlistEntry,
        role,
        isAdmin: role === "admin",
        canWrite: role === "admin" || role === "user",
        isExec: role === "executive",
        signIn: (email, password) =>
          signInWithEmailAndPassword(auth, email, password).then(() => undefined),
        sendPasswordReset: (email) => apiSendPasswordReset(email),
        signInWithSso: (_provider) => {
          throw new Error("SSO sign-in not yet wired — coming with Entra ID");
        },
        signOutUser: () => signOut(auth),
      };
    },
    [user, loading, allowlistEntry],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
